import { useEffect, useState } from 'react';
import { ApiError, inviteApi } from '../api';

/**
 * Aanmelden met een uitnodigingslink (/invite/CODE): naam en e-mail, daarna inloggen met Plex.
 * Na het inloggen volgt de welkomst-wizard.
 */
export function InviteScreen({ code: initial, onRegistered, onLoggedIn }: { code: string; onRegistered: (name: string) => void; onLoggedIn: () => void }) {
  const [code, setCode] = useState(initial);
  const [state, setState] = useState<'checking' | 'ok' | 'invalid'>(initial ? 'checking' : 'invalid');
  const [label, setLabel] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [access, setAccess] = useState<'plex' | 'addons' | 'choice'>('plex');
  // Bij "zelf kiezen": met of zonder Plex
  const [way, setWay] = useState<'plex' | 'local' | null>(null);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!initial) return;
    inviteApi.publicInfo(initial)
      .then((r) => { setLabel(r.label); setAccess(r.access ?? 'plex'); setState('ok'); })
      .catch((err) => { setState('invalid'); if (err instanceof ApiError && err.message !== err.code) setError(err.message); });
  }, [initial]);

  const local = access === 'addons' || (access === 'choice' && way === 'local');
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (local) {
        if (password !== password2) throw new ApiError(400, 'mismatch', 'De twee wachtwoorden zijn niet gelijk.');
        const r = await inviteApi.registerLocal({ code, name, email, password });
        if (r.pending) setPending(true);
        else onLoggedIn();
        return;
      }
      const r = await inviteApi.register({ code, name, email });
      onRegistered(r.name);
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Aanmelden mislukt. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="center-screen ob-screen">
      <div className="ob-card">
        <div className="logo">tu<span>media</span></div>
        <h1 className="ob-title">Je bent uitgenodigd</h1>
        <p className="lead muted">
          Films, series, live tv, sport en muziek op één plek.{label ? <> <br /><span className="ob-label">{label}</span></> : null}
        </p>
        {pending ? (
          <p className="lead">Bedankt voor je aanmelding, {name}! De beheerder moet je nog goedkeuren. Daarna log je in met je e-mailadres en wachtwoord.</p>
        ) : state === 'checking' ? (
          <p className="subtle" aria-busy="true">Uitnodiging controleren…</p>
        ) : state === 'ok' && access === 'choice' && !way ? (
          <div className="ob-choice" data-focus-group>
            <p className="lead">Hoe wil je tumedia gebruiken?</p>
            <button type="button" className="ob-choice-btn" onClick={() => setWay('plex')} data-autofocus>
              <strong>Met mijn Plex-account</strong>
              <span className="subtle">De bibliotheek van de beheerder, en eventueel addons erbij.</span>
            </button>
            <button type="button" className="ob-choice-btn" onClick={() => setWay('local')}>
              <strong>Zonder Plex</strong>
              <span className="subtle">Met e-mail en wachtwoord; je gebruikt addons met je eigen Real-Debrid of TorBox. Plex kun je later nog koppelen.</span>
            </button>
          </div>
        ) : (
          <form className="form-grid ob-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            <label>Naam<input className="text-input" required autoFocus value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={60} /></label>
            <label>E-mailadres<input className="text-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
            {local && (
              <>
                <label>Wachtwoord<input className="text-input" required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
                <label>Wachtwoord nog een keer<input className="text-input" required type="password" minLength={8} value={password2} onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password" /></label>
              </>
            )}
            <label>Uitnodigingscode<input className="text-input ob-code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoComplete="off" spellCheck={false} placeholder="ABCDE-12345" /></label>
            {error && <p className="error" role="alert">{error}</p>}
            {state === 'invalid' && !error && <p className="error" role="alert">Deze uitnodiging is ongeldig of verlopen. Controleer de code of vraag een nieuwe link.</p>}
            <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Bezig…' : 'Aanmelden'}</button>
            {local ? (
              <p className="subtle hint">Minstens 8 tekens. Je logt voortaan in met dit e-mailadres en wachtwoord.{access === 'choice' && <> <button type="button" className="linklike" onClick={() => setWay(null)}>Toch met Plex?</button></>}</p>
            ) : (
              <p className="subtle hint">Hierna log je in met je Plex-account. Heb je er nog geen? Maak gratis een account aan op <a href="https://www.plex.tv/sign-up/" target="_blank" rel="noreferrer">plex.tv</a>.{access === 'choice' && <> <button type="button" className="linklike" onClick={() => setWay(null)}>Toch zonder Plex?</button></>}</p>
            )}
          </form>
        )}
      </div>
    </main>
  );
}
